/* Les comptes agents et ce que chaque niveau voit. Réservé à la direction :
   les niveaux viennent de la base, et c'est la base qui les applique. */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgents, useCommissions, useVentes } from "@/lib/donnees";
import { useNiveau } from "@/lib/session";
import { useToast } from "@/ui/Toast";
import { inviter } from "./actions";
import { Banner, Empty, Kpi, Kpis, Section, Tag } from "@/ui/primitives";
import { Table, dansUnChamp } from "@/ui/Table";
import type { Colonne } from "@/ui/Table";
import { entier, euro, euroCourt } from "@/lib/format";
import type { NiveauAgent, Profil } from "@/types/metier";

const NOM_NIVEAU: Record<NiveauAgent, { nom: string; ton: string }> = {
  agent: { nom: "Agent", ton: "accent" },
  admin: { nom: "Direction", ton: "info" },
};

const DROITS: { quoi: string; agent: boolean; admin: boolean }[] = [
  { quoi: "Catalogue, prix de vente, listes de prix", agent: true, admin: true },
  { quoi: "Opportunités, devis, ventes, packaging, qualité", agent: true, admin: true },
  { quoi: "Fiches clients et contacts", agent: true, admin: true },
  { quoi: "Fiches usines, contacts et documents", agent: true, admin: true },
  { quoi: "Nom de l'usine derrière un produit", agent: true, admin: true },
  { quoi: "Prix d'achat usine et marge réelle", agent: false, admin: true },
  { quoi: "Propositions de prix des usines", agent: false, admin: true },
  { quoi: "Barème des commissions et parts des agents", agent: false, admin: true },
  { quoi: "Facturation et paramètres", agent: false, admin: true },
  { quoi: "Comptes et niveaux des agents", agent: false, admin: true },
];

/* Trois états, pas deux : sans compte la fiche attend une invitation, et c'est
   une information différente d'un compte suspendu. */
function etatCompte(a: Profil): { nom: string; ton: string } {
  if (!a.actif) return { nom: "suspendu", ton: "crit" };
  if (!a.compte_id) return { nom: a.invite_le ? "invité" : "sans compte", ton: "warn" };
  return { nom: "actif", ton: "ok" };
}

export function Agents() {
  const navigue = useNavigate();
  const niveau = useNiveau();
  const toast = useToast();
  const { donnees: agents, chargement, erreur, recharger } = useAgents();
  const { donnees: ventes } = useVentes();
  const { donnees: commissions } = useCommissions();
  const [envoi, setEnvoi] = useState<string | null>(null);
  const [panne, setPanne] = useState<string | null>(null);

  async function envoyer(a: Profil) {
    setEnvoi(a.id);
    setPanne(null);
    try {
      await inviter(a);
      toast(`Invitation envoyée à ${a.email}.`);
      recharger();
    } catch (e) {
      setPanne(e instanceof Error ? e.message : "L'invitation a échoué.");
    } finally {
      setEnvoi(null);
    }
  }

  const pipeline = useMemo(() => {
    const m = new Map<string, { n: number; montant: number }>();
    for (const v of ventes) {
      if (v.etape !== "devis") continue;
      const e = m.get(v.agent_id) ?? { n: 0, montant: 0 };
      e.n += 1;
      e.montant += Number(v.montant);
      m.set(v.agent_id, e);
    }
    return m;
  }, [ventes]);

  const du = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of commissions) {
      if (c.etat === "payee") continue;
      m.set(c.agent_id, (m.get(c.agent_id) ?? 0) + Number(c.agent_montant));
    }
    return m;
  }, [commissions]);

  if (niveau !== "admin") {
    return (
      <div className="pad">
        <Empty
          titre="Réservé à la direction"
          detail="Les comptes et les niveaux se gèrent depuis un compte de direction."
        />
      </div>
    );
  }

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  const actifs = agents.filter((a) => a.actif);
  const porte = [...pipeline.values()].reduce((s, e) => s + e.montant, 0);

  const marque = (v: boolean) =>
    v ? <span className="yes">oui</span> : <span className="no">non</span>;

  const colonnes: Colonne<Profil>[] = [
    {
      cle: "nom",
      titre: "Agent",
      filtre: "texte",
      sous: (a) => a.email,
      rendu: (a) => (
        <span className="cell-duo">
          <span className="avatar">
            {a.nom
              .split(" ")
              .map((m) => m[0] ?? "")
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </span>
          <span>{a.nom}</span>
        </span>
      ),
    },
    { cle: "email", titre: "Adresse", type: "ref", filtre: "texte", mobile: false },
    {
      cle: "niveau",
      titre: "Niveau",
      filtre: "select",
      texte: (a) => NOM_NIVEAU[a.niveau].nom,
      rendu: (a) => <Tag ton={NOM_NIVEAU[a.niveau].ton}>{NOM_NIVEAU[a.niveau].nom}</Tag>,
    },
    { cle: "opportunites", titre: "Opportunités", court: "Opp.", type: "num", total: true,
      mobile: false, nombre: (a) => pipeline.get(a.id)?.n ?? 0 },
    { cle: "pipeline", titre: "Pipeline", type: "euro", total: true,
      nombre: (a) => pipeline.get(a.id)?.montant ?? 0 },
    { cle: "du", titre: "Commissions dues", court: "Dû", type: "euro", total: true, mobile: false,
      nombre: (a) => du.get(a.id) ?? 0 },
    {
      cle: "actif",
      titre: "Compte",
      filtre: "select",
      texte: (a) => etatCompte(a).nom,
      rendu: (a) => {
        const e = etatCompte(a);
        return <Tag ton={e.ton} dot>{e.nom}</Tag>;
      },
    },
    {
      cle: "inviter",
      titre: "",
      court: "",
      mobile: false,
      rendu: (a) =>
        a.compte_id ? (
          <span className="no">—</span>
        ) : (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={envoi === a.id}
            onClick={() => void envoyer(a)}
          >
            {envoi === a.id ? "Envoi…" : a.invite_le ? "Relancer" : "Inviter"}
          </button>
        ),
    },
  ];

  return (
    <div className="pad">
      <Kpis>
        <Kpi k="Comptes actifs" v={entier(actifs.length)} n={`${agents.length} fiches au total`} />
        <Kpi k="Pipeline porté" v={euroCourt(porte)} n="devis en cours, tous agents" />
        <Kpi
          k="Commissions dues"
          v={euroCourt([...du.values()].reduce((s, v) => s + v, 0))}
          n="parts agents non réglées"
        />
      </Kpis>

      {panne ? <div className="banner ko">{panne}</div> : null}

      <Section titre="Agents" note={chargement ? "chargement…" : `${agents.length} fiches`}>
        <Table
          lignes={agents}
          cle={(a) => a.id}
          colonnes={colonnes}
          chercher="Nom ou adresse"
          onClic={(a, ev) => {
            if (!dansUnChamp(ev)) navigue(`/agents/${a.id}`);
          }}
        />
      </Section>

      <Section titre="Ce que voit chaque niveau" note="appliqué par la base, pas par l'écran">
        <div className="card">
          <div className="tablewrap">
            <table className="matrix">
              <thead>
                <tr>
                  <th>Accès</th>
                  <th className="c">Agent</th>
                  <th className="c">Direction</th>
                </tr>
              </thead>
              <tbody>
                {DROITS.map((d) => (
                  <tr key={d.quoi}>
                    <td>{d.quoi}</td>
                    <td className="c">{marque(d.agent)}</td>
                    <td className="c">{marque(d.admin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Banner>
        Une fiche existe avant son compte : on l'importe, puis on l'invite. L'agent reçoit un
        lien, choisit son mot de passe, et sa fiche se rattache à lui à la première connexion —
        par son adresse, qui seule fait foi. Le mailer de Supabase plafonne à deux envois par
        heure tant qu'aucun SMTP n'est branché, d'où l'invitation fiche par fiche. Total des
        parts non réglées : <b className="mono">{euro([...du.values()].reduce((s, v) => s + v, 0))}</b>.
      </Banner>
    </div>
  );
}
